import type { CreateBlacklistAutoRule } from '@hprofits/api';
import { blacklistAutoRuleKeys, useCreateBlacklistAutoRule, useUpdateBlacklistAutoRule } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import type { BlacklistAutoRuleId } from '@hprofits/shared';
import { useQuery } from '@tanstack/vue-query';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';

import { apiClient } from '@/api-client';

export const useBlacklistEditorQueries = (id: ComputedRef<BlacklistAutoRuleId | null>) => {
  // `useBlacklistAutoRule` needs a non-null id to build its key; `/new` has none, so the query is
  // built here on the same key the api-client mutations invalidate and evict. A failed load is
  // reported in the editor's own error panel, so the global error toast stays off.
  const rule = useQuery({
    ...SUPPRESS_ERROR_TOAST_META,
    queryKey: computed(() => (id.value === null ? blacklistAutoRuleKeys.all : blacklistAutoRuleKeys.detail(id.value))),
    enabled: () => id.value !== null,
    queryFn: async () => {
      const ruleId = id.value;
      if (ruleId === null) {
        throw new Error('blacklist rule query ran without a rule id');
      }
      return apiClient.blacklistAutoRule.findOne({ ruleId });
    },
  });
  // Same for a failed save.
  const create = useCreateBlacklistAutoRule(apiClient, SUPPRESS_ERROR_TOAST_META);
  const update = useUpdateBlacklistAutoRule(apiClient, SUPPRESS_ERROR_TOAST_META);
  const save = async (body: CreateBlacklistAutoRule): Promise<void> => {
    const ruleId = id.value;
    if (ruleId === null) {
      await create.mutateAsync({ body });
      return;
    }
    await update.mutateAsync({ ruleId, body });
  };
  return { rule, save };
};
